import { Box, Flex, Separator } from '@volue/wave-react';
import React from 'react';

import type { LoadFlowScenarioResults } from '@/features/execute-calculation-set/overmind/calculation';
import type { MvLoadFlowSummaryEvaluatedResults } from '@/features/execute-calculation-set/overmind/calculation/derived-state/loadFlowSummary/mediumVoltage/MvLoadFlowSummaryEvaluatedResults';

import { LoadFlowAttentionWarningCounter } from '../LoadFlowAttentionWarningCounter';
import { EvaluatedCalculationResult } from './EvaluatedCalculationResult';
import { EvaluatedCalculationResultRange } from './EvaluatedCalculationResultRange';

export type NonEmptyArray<T> = [T, ...T[]];
export type MvLoadFlowSummaryCalculationResultsProps = {
  loadFlowScenarios: NonEmptyArray<LoadFlowScenarioResults>;
  loadFlowSummary: MvLoadFlowSummaryEvaluatedResults;
};

export function MvLoadFlowSummaryCalculationResults(
  props: MvLoadFlowSummaryCalculationResultsProps
) {
  return (
    <Box>
      <Box
        role={'heading'}
        css={{
          color: '$colorAccent70',
          fontWeight: 'bold',
          fontSize: 'larger'
        }}
      >
        <Flex main={'space-between'}>
          <Box>
            {'LoadFlow'}
            <span data-testid={'loadflow-scenarios-counter'} className="mLs">
              {getScenariosCounter(props.loadFlowScenarios)}
            </span>
          </Box>
          <LoadFlowAttentionWarningCounter />
        </Flex>
      </Box>

      <Flex flow="column" gap={'spacingXs'}>
        <EvaluatedCalculationResult
          value={props.loadFlowSummary.externalNetworkInjectionVoltageSummary.secondary
            .toKilo()
            .toString()}
          label="Starting Voltage secondary"
          status="NO_ATTENTION_REQUIRED"
        />
        <Separator />

        <EvaluatedCalculationResultRange
          min={{
            value:
              props.loadFlowSummary.busbarVoltageSummary.min.value.toString(),
            status: props.loadFlowSummary.busbarVoltageSummary.min.status
          }}
          max={{
            value:
              props.loadFlowSummary.busbarVoltageSummary.max.value.toString(),
            status: props.loadFlowSummary.busbarVoltageSummary.max.status
          }}
          label={'MV Busbar Voltages'}
        />

        <EvaluatedCalculationResultRange
          // voltage drop should be added and used here instead voltage diif
          // https://github.com/Volue/pg-team-nsa/issues/749
          min={{
            value:
              props.loadFlowSummary.busbarVoltageDiffTransformerEndPercentSummary.min.value.toString(
                { withUnit: true }
              ),
            status:
              props.loadFlowSummary
                .busbarVoltageDiffTransformerEndPercentSummary.min.status
          }}
          max={{
            value:
              props.loadFlowSummary.busbarVoltageDiffTransformerEndPercentSummary.max.value.toString(
                { withUnit: true }
              ),
            status:
              props.loadFlowSummary
                .busbarVoltageDiffTransformerEndPercentSummary.max.status
          }}
          label={'MV Busbar Voltage Drops'}
        />

        <EvaluatedCalculationResultRange
          // voltage drop should be added and used here instead voltage diif
          // https://github.com/Volue/pg-team-nsa/issues/749
          min={{
            value:
              props.loadFlowSummary.energyConsumerVoltagesSummary.min.value.toString(),
            status:
              props.loadFlowSummary.energyConsumerVoltagesSummary.min.status
          }}
          max={{
            value:
              props.loadFlowSummary.energyConsumerVoltagesSummary.max.value.toString(),
            status:
              props.loadFlowSummary.energyConsumerVoltagesSummary.max.status
          }}
          label={'MV Energy Consumer Voltages'}
        />

        <EvaluatedCalculationResultRange
          min={{
            value:
              props.loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercentSummary.min.value.toString(),
            status:
              props.loadFlowSummary
                .energyConsumerVoltageDiffTransformerEndPercentSummary.min
                .status
          }}
          max={{
            value:
              props.loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercentSummary.max.value.toString(),
            status:
              props.loadFlowSummary
                .energyConsumerVoltageDiffTransformerEndPercentSummary.max
                .status
          }}
          label={'MV Energy Consumer Voltage Drops'}
        />

        <EvaluatedCalculationResultRange
          min={{
            value:
              props.loadFlowSummary.acLineSegmentLoadingSummary.min.value.toString(),
            status: props.loadFlowSummary.acLineSegmentLoadingSummary.min.status
          }}
          max={{
            value:
              props.loadFlowSummary.acLineSegmentLoadingSummary.max.value.toString(),
            status: props.loadFlowSummary.acLineSegmentLoadingSummary.max.status
          }}
          label={'ACLineSegment loadings'}
        />

        <EvaluatedCalculationResultRange
          min={{
            value:
              props.loadFlowSummary.powerTransformerloadingPercentSummary.min.value.toString(),
            status:
              props.loadFlowSummary.powerTransformerloadingPercentSummary.min
                .status
          }}
          max={{
            value:
              props.loadFlowSummary.powerTransformerloadingPercentSummary.max.value.toString(),
            status:
              props.loadFlowSummary.powerTransformerloadingPercentSummary.max
                .status
          }}
          label={'MV transformer loadings'}
        />

        <Separator />

        <EvaluatedCalculationResult
          value={props.loadFlowSummary.activePowerSummary.toKilo().toString()}
          label="Sum Active Power"
          status="NO_ATTENTION_REQUIRED"
        />

        <EvaluatedCalculationResult
          value={props.loadFlowSummary.reactivePowerSummary.toKilo().toString()}
          label="Sum Reactive Power"
          status="NO_ATTENTION_REQUIRED"
        />
      </Flex>
    </Box>
  );
}

function getScenariosCounter(
  loadFlowScenarios: NonEmptyArray<LoadFlowScenarioResults>
) {
  const scenariosCounter = loadFlowScenarios.length;

  const scenariosCounterContent =
    scenariosCounter === 1 ? '1 scenario' : `${scenariosCounter} scenarios`;

  return `(${scenariosCounterContent})`;
}
